import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { useId, useRef } from 'react';

import { pauseGsapWhenHidden } from '@/lib/motion/gsap';

/**
 * =============================================================================
 *  「小芽」—— AI 理財助理的吉祥物（ADR-0004 前端側）。
 *
 *  圓角方頭、墨色面罩裡兩顆綠色膠囊眼、頭頂一株嫩芽天線。純 inline SVG（64×64 viewBox），
 *  動畫全部由 GSAP 在 root ref 範圍內驅動，每個狀態是 gsap.matchMedia() 裡的一組 tween：
 *
 *    idle      呼吸（scale 1→1.03，3.2 s 來回）＋不規則眨眼＋葉子左右擺 ±6°
 *    thinking  眼睛看右上、頭歪 4°、三顆點繞著嫩芽轉、面罩光澤掃過
 *    speaking  嘴巴隨機開合、眼睛瞇成笑眼
 *    success   跳一下（擠壓／拉伸）、眼睛變 ^ ^、三顆小星星，0.8 s 後回到 idle
 *
 *  prefers-reduced-motion：沒有循環與跳躍，每個狀態一個靜態姿勢，只允許 ≤0.2 s 的淡入淡出。
 *  分頁在背景時整條全域時間軸暫停（lib/motion/gsap.ts）。
 *
 *  無障礙：預設 role="img" 並以 aria-label 說出狀態；當作訊息頭像等裝飾時 `decorative`
 *  → aria-hidden。狀態變化由對話的 aria-live 區域播報，不由吉祥物播報。
 * ========================================================================== */

export type MascotState = 'idle' | 'thinking' | 'speaking' | 'success';
export type MascotSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_PX: Record<MascotSize, number> = { sm: 28, md: 48, lg: 96, xl: 160 };

const STATE_LABEL: Record<MascotState, string> = {
  idle: 'AI 理財助理小芽',
  thinking: 'AI 理財助理小芽（思考中）',
  speaking: 'AI 理財助理小芽（回答中）',
  success: 'AI 理財助理小芽（完成）',
};

const GREEN = '#48C74E';
const LEAF = '#A7E3A9';
const INK = '#18181B';
const CREAM = '#FFF8EC';
const CHEEK = '#FFD6CC';

export interface MascotProps {
  state: MascotState;
  size: MascotSize;
  /** 訊息頭像之類純裝飾的位置：不進無障礙樹。 */
  decorative?: boolean;
  /** 不跑循環動畫，只擺該狀態的靜態姿勢（一整串訊息頭像同時呼吸眨眼只是雜訊）。 */
  still?: boolean;
  className?: string;
}

export function Mascot({
  state,
  size,
  decorative = false,
  still = false,
  className = '',
}: MascotProps) {
  const rootRef = useRef<HTMLSpanElement>(null);
  const clipId = `mascot-visor-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const px = SIZE_PX[size];

  useGSAP(
    () => {
      if (still) {
        staticPose(state);
        return;
      }
      pauseGsapWhenHidden();
      const mm = gsap.matchMedia();
      mm.add(
        {
          motion: '(prefers-reduced-motion: no-preference)',
          reduce: '(prefers-reduced-motion: reduce)',
        },
        (context) => {
          if (context.conditions?.reduce === true) {
            staticPose(state);
            return;
          }
          if (state === 'success') {
            // 一次性的跳躍結束後才開始待機循環；ctx.add 讓那些循環仍歸這個 context 管，
            // 狀態再變或卸載時一起 revert。
            playSuccess(() => {
              context.add(() => {
                playIdle();
              });
            });
          } else if (state === 'thinking') {
            playThinking();
          } else if (state === 'speaking') {
            playSpeaking();
          } else {
            playIdle();
          }
        },
        rootRef,
      );
    },
    { scope: rootRef, dependencies: [state, still], revertOnUpdate: true },
  );

  return (
    <span
      ref={rootRef}
      className={`inline-block shrink-0 ${className}`}
      style={{ width: px, height: px }}
      {...(decorative
        ? { 'aria-hidden': true }
        : { role: 'img', 'aria-label': STATE_LABEL[state] })}
      data-mascot-state={state}
    >
      <svg
        viewBox="0 0 64 64"
        width={px}
        height={px}
        aria-hidden="true"
        focusable="false"
        className="block overflow-visible"
      >
        <defs>
          <clipPath id={clipId}>
            <rect x="13" y="23" width="38" height="17" rx="8.5" />
          </clipPath>
        </defs>

        <ellipse className="m-shadow" cx="32" cy="60.5" rx="15" ry="2.5" fill={INK} opacity="0.08" />

        <g className="m-body">
          {/* 思考中的三顆點：畫在頭後面，繞到下半圈時被頭擋住，看起來像在繞著嫩芽轉。 */}
          <g className="m-dots" opacity="0">
            <circle cx="40" cy="8" r="1.5" fill={GREEN} />
            <circle cx="28" cy="1.1" r="1.5" fill={GREEN} opacity="0.7" />
            <circle cx="28" cy="14.9" r="1.5" fill={GREEN} opacity="0.45" />
          </g>

          <g className="m-sprout">
            <path d="M32 15.5V9.5" stroke={GREEN} strokeWidth="2" strokeLinecap="round" fill="none" />
            <g className="m-leaves">
              <path d="M32 10.5C28 10.5 25.3 8 24.8 4.6C28.6 4.6 31.6 6.8 32 10.5Z" fill={GREEN} />
              <path d="M32 9.6C36.1 9.9 39.2 7.3 39.6 3.6C35.6 3.6 32.4 5.8 32 9.6Z" fill={LEAF} />
            </g>
          </g>

          <rect
            x="7"
            y="14"
            width="50"
            height="43"
            rx="18"
            fill={CREAM}
            stroke={INK}
            strokeOpacity="0.1"
            strokeWidth="1.5"
          />

          <rect x="13" y="23" width="38" height="17" rx="8.5" fill={INK} />
          <g clipPath={`url(#${clipId})`}>
            <path className="m-sheen" d="M15 23h5l-4 17h-5z" fill="#FFFFFF" opacity="0.12" />
          </g>

          <g className="m-eyes">
            <rect className="m-eye" x="21.5" y="27" width="5" height="9" rx="2.5" fill={GREEN} />
            <rect className="m-eye" x="37.5" y="27" width="5" height="9" rx="2.5" fill={GREEN} />
            <g
              className="m-happy"
              opacity="0"
              stroke={GREEN}
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            >
              <path d="M20.5 33.5L24 30L27.5 33.5" />
              <path d="M36.5 33.5L40 30L43.5 33.5" />
            </g>
          </g>

          <circle cx="15.5" cy="45" r="2.6" fill={CHEEK} />
          <circle cx="48.5" cy="45" r="2.6" fill={CHEEK} />
          <rect className="m-mouth" x="28" y="44" width="8" height="4" rx="2" fill={INK} fillOpacity="0.75" />
        </g>

        <g fill={GREEN}>
          <path className="m-spark" opacity="0" d="M8 10.5Q8 14 11.5 14Q8 14 8 17.5Q8 14 4.5 14Q8 14 8 10.5Z" />
          <path className="m-spark" opacity="0" d="M56 8.5Q56 12 59.5 12Q56 12 56 15.5Q56 12 52.5 12Q56 12 56 8.5Z" />
          <path className="m-spark" opacity="0" d="M58 30.5Q58 34 61.5 34Q58 34 58 37.5Q58 34 54.5 34Q58 34 58 30.5Z" />
        </g>
      </svg>
    </span>
  );
}

/* ----------------------------------------------------------------------------
 *  以下每個函式都在 matchMedia 的 context 裡被呼叫：選擇器限定在 root ref 內，
 *  建出來的 tween 由 context 收集，狀態改變或卸載時 revert 回 SVG 原始屬性。
 * ------------------------------------------------------------------------- */

function playIdle() {
  gsap.to('.m-body', {
    scale: 1.03,
    transformOrigin: '50% 100%',
    duration: 1.6,
    ease: 'sine.inOut',
    yoyo: true,
    repeat: -1,
  });
  swayLeaves();

  // 眨眼：一條不等距的眨眼時間表（每次間隔 2.5–6 s），整條循環播放。
  // 用固定的時間軸而不是遞迴的 delayedCall——後者建出來的 tween 不在 context 裡，revert 收不到。
  const blink = gsap.timeline({ repeat: -1 });
  let at = 0;
  for (let i = 0; i < 6; i++) {
    at += gsap.utils.random(2.5, 6);
    blink
      .to('.m-eye', { scaleY: 0.1, transformOrigin: '50% 50%', duration: 0.07, ease: 'power1.in' }, at)
      .to('.m-eye', { scaleY: 1, duration: 0.07, ease: 'power1.out' }, at + 0.07);
  }
  blink.to({}, { duration: gsap.utils.random(2.5, 6) - 0.14 }, at + 0.14);
}

function swayLeaves() {
  gsap.fromTo(
    '.m-leaves',
    { rotation: -6, svgOrigin: '32 10.5' },
    { rotation: 6, svgOrigin: '32 10.5', duration: 1.2, ease: 'sine.inOut', yoyo: true, repeat: -1 },
  );
}

function playThinking() {
  gsap.to('.m-eyes', { x: 1.8, y: -1.8, duration: 0.3, ease: 'power2.out' });
  gsap.to('.m-body', { rotation: 4, svgOrigin: '32 57', duration: 0.4, ease: 'power2.out' });
  gsap.to('.m-dots', { opacity: 1, duration: 0.2 });
  gsap.to('.m-dots', { rotation: 360, svgOrigin: '32 8', duration: 1.2, ease: 'none', repeat: -1 });
  gsap.fromTo(
    '.m-sheen',
    { x: 0 },
    { x: 40, duration: 1.1, ease: 'power1.inOut', repeat: -1, repeatDelay: 0.5 },
  );
  swayLeaves();
}

function playSpeaking() {
  gsap.to('.m-eye', { scaleY: 0.7, transformOrigin: '50% 50%', duration: 0.15 });
  gsap.to('.m-mouth', {
    scaleY: () => gsap.utils.random(0.35, 1),
    duration: () => gsap.utils.random(0.12, 0.2),
    transformOrigin: '50% 50%',
    ease: 'sine.inOut',
    repeat: -1,
    repeatRefresh: true,
  });
  swayLeaves();
}

function playSuccess(onDone: () => void) {
  gsap
    .timeline({ onComplete: onDone })
    .to('.m-eye', { opacity: 0, duration: 0.1 }, 0)
    .to('.m-happy', { opacity: 1, duration: 0.1 }, 0)
    .to('.m-body', { scaleX: 1.06, scaleY: 0.92, transformOrigin: '50% 100%', duration: 0.12, ease: 'power2.in' }, 0)
    .to('.m-body', { y: -6, scaleX: 0.96, scaleY: 1.06, duration: 0.2, ease: 'power2.out' }, 0.12)
    .to('.m-shadow', { scale: 0.8, opacity: 0.05, transformOrigin: '50% 50%', duration: 0.2 }, 0.12)
    .to('.m-body', { y: 0, scaleX: 1, scaleY: 1, duration: 0.22, ease: 'power2.in' }, 0.32)
    .to('.m-shadow', { scale: 1, opacity: 0.08, duration: 0.22 }, 0.32)
    .fromTo(
      '.m-spark',
      { opacity: 0, scale: 0.4, transformOrigin: '50% 50%' },
      { opacity: 1, scale: 1, duration: 0.18, stagger: 0.06 },
      0.15,
    )
    .to('.m-spark', { opacity: 0, duration: 0.2 }, 0.55)
    .to('.m-happy', { opacity: 0, duration: 0.12 }, 0.68)
    .to('.m-eye', { opacity: 1, duration: 0.12 }, 0.68);
}

/** 減少動態：每個狀態一個靜態姿勢，只有 0.2 s 的淡入淡出。 */
function staticPose(state: MascotState) {
  switch (state) {
    case 'thinking':
      gsap.set('.m-eyes', { x: 1.8, y: -1.8 });
      gsap.to('.m-dots', { opacity: 1, duration: 0.2 });
      return;
    case 'speaking':
      gsap.set('.m-eye', { scaleY: 0.7, transformOrigin: '50% 50%' });
      gsap.set('.m-mouth', { scaleY: 0.6, transformOrigin: '50% 50%' });
      return;
    case 'success':
      gsap.to('.m-eye', { opacity: 0, duration: 0.2 });
      gsap.to('.m-happy', { opacity: 1, duration: 0.2 });
      return;
    case 'idle':
      return;
  }
}
